import { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import Badge from './Badge';
import { useConfirm, useToast } from '../context/FeedbackContext';

function fmt(n) {
  return n != null ? '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
}

const STATUS_BADGE = {
  matched: { variant: 'success', label: '✓ Matched' },
  split: { variant: 'info', label: '✓ Split combined' },
  typo: { variant: 'warning', label: '⚠ Check match' },
  unmatched: { variant: 'danger', label: '⚠ No match — saved to Pending' },
  already_imported: { variant: 'neutral', label: '— Already imported' },
  already_paid: { variant: 'neutral', label: '— Already paid' },
};

export default function HexPaymentImportModal({ onClose, onImported }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [tab, setTab] = useState('upload');
  const [step, setStep] = useState('upload');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [epdByRow, setEpdByRow] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [pendingItems, setPendingItems] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState('');
  const [mismatchForm, setMismatchForm] = useState({});
  // EPD % for a pending item: what the user typed, else the rate the import
  // preview detected on Hexaware's report (saved as epd_suggestion_rate, a
  // fraction). Used for BOTH the input's value and the Resolve payload, so a
  // pre-filled rate is never shown without actually being applied.
  const epdPctFor = item => {
    const typed = mismatchForm[item.id]?.epd_rate;
    if (typed !== undefined) return typed;
    return item.epd_suggestion_rate > 0 ? String(+(item.epd_suggestion_rate * 100).toFixed(4)) : '';
  };
  // --- BGV Pending: deductions with a name but no invoice number - always
  // require a human to pick the real contractor + WO, never auto-created.
  const [bgvPending, setBgvPending] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [bgvForm, setBgvForm] = useState({});
  const [bgvBusyId, setBgvBusyId] = useState(null);

  useEffect(() => {
    loadPending();
    loadBgvPending();
    api.get('/work-orders').then(r => setWorkOrders(r.data)).catch(() => {});
  }, []);

  function loadPending() {
    api.get('/hex-payment/pending').then(r => setPendingItems(r.data)).catch(() => {});
  }

  function loadBgvPending() {
    api.get('/hex-payment/bgv-pending').then(r => {
      setBgvPending(r.data);
      setBgvForm(f => {
        const next = { ...f };
        for (const item of r.data) {
          if (!next[item.id]) {
            const topMatch = item.suggestions && item.suggestions[0];
            next[item.id] = { contractor_id: topMatch ? String(topMatch.id) : '', wo_id: '' };
          }
        }
        return next;
      });
    }).catch(() => {});
  }

  const contractorOptions = useMemo(() => {
    const seen = new Map();
    for (const w of workOrders) {
      if (!seen.has(w.contractor_id)) seen.set(w.contractor_id, w.contractor_name);
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [workOrders]);

  function woOptionsFor(contractorId) {
    return workOrders.filter(w => String(w.contractor_id) === String(contractorId));
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true); setError(''); setPreview(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/hex-payment/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
      const prefill = {};
      for (const row of r.data.preview || []) {
        if (row.epd_suggestion) {
          prefill[row.normalized] = { epd_rate: (row.epd_suggestion.epd_pct / 100).toString(), bgc_amount: '' };
        }
      }
      setEpdByRow(prefill);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to parse PDF');
    }
    setLoading(false);
    e.target.value = '';
  }

  async function handleCommit() {
    setLoading(true); setError('');
    const items = preview.preview
      .filter(p => p.status !== 'already_imported' && p.status !== 'already_paid')
      .map(p => ({
        normalized: p.normalized,
        raw_invoices: p.raw_invoices,
        amount: p.amount,
        matched_id: p.matched_id,
        epd_rate: epdByRow[p.normalized]?.epd_rate || '0',
        bgc_amount: epdByRow[p.normalized]?.bgc_amount || '',
      }));
    try {
      const r = await api.post('/hex-payment/commit', {
        pay_ref: preview.pay_ref,
        pay_date: preview.pay_date,
        pdf_total: preview.pdf_total,
        items,
        bgv_lines: preview.bgv_lines || [],
        stored_file: preview.stored_file || null,
      });
      setResult(r.data);
      setStep('done');
      loadPending();
      loadBgvPending();
      if (onImported) onImported();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    }
    setLoading(false);
  }

  async function handleRetry() {
    setRetrying(true); setRetryResult('');
    try {
      const r = await api.post('/hex-payment/pending/retry');
      setRetryResult(`✓ ${r.data.message}`);
      loadPending();
      if (onImported) onImported();
    } catch (e) {
      setRetryResult('✗ ' + (e.response?.data?.error || 'Failed'));
    }
    setRetrying(false);
  }

  async function handleSkipPending(itemId) {
    if (!(await confirm({ title: 'Skip this item?', message: 'The payment will not be recorded.', confirmLabel: 'Skip' }))) return;
    try {
      await api.post(`/hex-payment/pending/${itemId}/skip`);
      loadPending();
    } catch (e) {
      toast(e.response?.data?.error || 'Failed', 'error');
    }
  }

  async function handleResolveBgv(item) {
    const form = bgvForm[item.id] || {};
    if (!form.contractor_id || !form.wo_id) {
      toast('Please select both a contractor and a Work Order.', 'error');
      return;
    }
    setBgvBusyId(item.id);
    try {
      await api.post(`/hex-payment/bgv-pending/${item.id}/resolve`, {
        contractor_id: Number(form.contractor_id),
        wo_id: Number(form.wo_id),
      });
      loadBgvPending();
      if (onImported) onImported();
    } catch (e) {
      toast(e.response?.data?.error || 'Failed to create BGV invoice', 'error');
    }
    setBgvBusyId(null);
  }

  async function handleSkipBgv(itemId) {
    if (!(await confirm({ title: 'Skip this BGV deduction?', message: 'No invoice will be created for it.', confirmLabel: 'Skip' }))) return;
    try {
      await api.post(`/hex-payment/bgv-pending/${itemId}/skip`);
      loadBgvPending();
    } catch (e) {
      toast(e.response?.data?.error || 'Failed', 'error');
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel" style={{ width: 700 }}>
        <div className="drawer-header">
          <div>
            <h3 className="drawer-title">Hexaware Payment PDF Import</h3>
            {step === 'preview' && preview && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                Batch: <strong>{preview.pay_ref}</strong> · Date: <strong>{preview.pay_date}</strong> · Total: <strong>{fmt(preview.pdf_total)}</strong>
              </div>
            )}
          </div>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        {step === 'upload' && (
          <div className="panel-tabs">
            <button className={'panel-tab' + (tab === 'upload' ? ' active' : '')} onClick={() => setTab('upload')}>Upload PDF</button>
            <button className={'panel-tab' + (tab === 'pending' ? ' active' : '')} onClick={() => setTab('pending')}>
              Pending Items{pendingItems.length > 0 ? ` (${pendingItems.length})` : ''}
            </button>
            <button className={'panel-tab' + (tab === 'bgv' ? ' active' : '')} onClick={() => setTab('bgv')}>
              BGV Pending{bgvPending.length > 0 ? ` (${bgvPending.length})` : ''}
            </button>
          </div>
        )}

        <div className="panel-body">
          {error && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          {step === 'upload' && tab === 'upload' && (
            <div>
              <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                {loading ? 'Parsing…' : '↑ Upload Hexaware Remittance PDF'}
                <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleUpload} />
              </label>
              <p className="form-hint" style={{ marginTop: 10 }}>
                Upload the Hexaware payment remittance PDF. Invoices will be matched by number; EPD deductions found in the PDF are shown as suggestions to confirm before import. BGV deductions (a name, not an invoice number) are saved to the BGV Pending tab for manual contractor/WO assignment.
              </p>
            </div>
          )}

          {step === 'upload' && tab === 'pending' && (
            <div>
              {pendingItems.length === 0 ? (
                <div className="form-hint">No pending items.</div>
              ) : (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={handleRetry} disabled={retrying} style={{ marginBottom: 10 }}>
                    {retrying ? 'Retrying…' : 'Auto-retry all pending'}
                  </button>
                  {retryResult && <div className="form-hint" style={{ marginBottom: 10 }}>{retryResult}</div>}
                  {pendingItems.map(item => (
                    <div key={item.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 8, fontSize: 12 }}>
                      <div style={{ fontWeight: 700 }}>{item.raw_invoices}</div>
                      <div style={{ color: 'var(--muted)', marginBottom: 6 }}>
                        Normalized: {item.normalized} · {fmt(item.amount)} · Batch {item.pay_ref_no} · {item.pay_date}
                      </div>
                      {item.reason === 'amount_mismatch' && item.expected_ar_id ? (
                        <>
                          <div style={{ color: 'var(--danger-text)', marginBottom: 6 }}>
                            Mismatch — invoice {item.expected_invoice_number} is for {fmt(item.expected_amount)}, Hexaware paid {fmt(item.amount)} ({item.amount > item.expected_amount ? 'overpayment' : 'underpayment'})
                          </div>
                          {item.epd_suggestion_rate > 0 && (
                            <div style={{ color: 'var(--muted)', marginBottom: 6 }}>
                              EPD detected on Hexaware's report: {+(item.epd_suggestion_rate * 100).toFixed(4)}% - pre-filled below; clear it if it doesn't apply.
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                            <input type="number" step="0.01" placeholder="EPD %" style={{ width: 80 }}
                              value={epdPctFor(item)}
                              onChange={e => setMismatchForm(f => ({ ...f, [item.id]: { ...f[item.id], epd_rate: e.target.value } }))} />
                            <input type="number" step="0.01" placeholder="BGC $" style={{ width: 90 }}
                              value={mismatchForm[item.id]?.bgc_amount || ''}
                              onChange={e => setMismatchForm(f => ({ ...f, [item.id]: { ...f[item.id], bgc_amount: e.target.value } }))} />
                          </div>
                          <button className="btn btn-primary btn-sm" onClick={async () => {
                            const form = mismatchForm[item.id] || {};
                            try {
                              await api.post(`/hex-payment/pending/${item.id}/resolve`, {
                                ar_invoice_id: item.expected_ar_id,
                                epd_rate: epdPctFor(item) ? Number(epdPctFor(item)) / 100 : 0,
                                bgc_amount: form.bgc_amount ? Number(form.bgc_amount) : 0,
                              });
                              loadPending();
                            } catch (err) {
                              toast(err.response?.data?.error || 'Failed to post payment', 'error');
                            }
                          }} style={{ marginRight: 6 }}>
                            Post Payment
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleSkipPending(item.id)}>Skip</button>
                        </>
                      ) : (
                        <button className="btn btn-ghost btn-sm" onClick={() => handleSkipPending(item.id)}>Skip</button>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {step === 'upload' && tab === 'bgv' && (
            <div>
              {bgvPending.length === 0 ? (
                <div className="form-hint">No BGV deductions awaiting review.</div>
              ) : (
                bgvPending.map(item => {
                  const form = bgvForm[item.id] || { contractor_id: '', wo_id: '' };
                  const wos = woOptionsFor(form.contractor_id);
                  const busy = bgvBusyId === item.id;
                  return (
                    <div key={item.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 8, fontSize: 12 }}>
                      <div style={{ fontWeight: 700 }}>BGV — "{item.raw_name}"</div>
                      <div style={{ color: 'var(--muted)', marginBottom: 8 }}>
                        {fmt(item.amount)} · Batch {item.pay_ref_no} · {item.pay_date}
                        {item.suggestions && item.suggestions.length > 0 && (
                          <span> · Suggested: {item.suggestions.map(s => s.name).join(', ')}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <select
                          style={{ flex: 1 }}
                          value={form.contractor_id}
                          onChange={e => setBgvForm(f => ({ ...f, [item.id]: { contractor_id: e.target.value, wo_id: '' } }))}
                        >
                          <option value="">Select contractor…</option>
                          {contractorOptions.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                        <select
                          style={{ flex: 1 }}
                          value={form.wo_id}
                          disabled={!form.contractor_id}
                          onChange={e => setBgvForm(f => ({ ...f, [item.id]: { ...f[item.id], wo_id: e.target.value } }))}
                        >
                          <option value="">Select Work Order…</option>
                          {wos.map(w => (
                            <option key={w.id} value={w.id}>{w.client_wo_number} ({w.status})</option>
                          ))}
                        </select>
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={() => handleResolveBgv(item)} disabled={busy} style={{ marginRight: 6 }}>
                        {busy ? 'Creating…' : 'Create BGV Invoice'}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleSkipBgv(item.id)}>Skip</button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {step === 'preview' && preview && (
            <div>
              {preview.duplicate_batch && (
                <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
                  This batch ({preview.pay_ref}) was already imported on {preview.duplicate_batch.imported_at} ({preview.duplicate_batch.count} invoices). Re-importing will skip already-recorded payments.
                </div>
              )}
              {preview.bgv_lines && preview.bgv_lines.length > 0 && (
                <div style={{ background: 'var(--info-bg)', color: 'var(--info-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
                  This batch has {preview.bgv_lines.length} BGV deduction{preview.bgv_lines.length > 1 ? 's' : ''} (total {fmt(preview.bgv_lines.reduce((s, b) => s + b.amount, 0))}). They'll be saved to the BGV Pending tab for you to assign a contractor and Work Order after importing.
                </div>
              )}
              {preview.preview.map((p, i) => {
                const badge = STATUS_BADGE[p.status] || STATUS_BADGE.unmatched;
                return (
                  <div key={i} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 8, fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 700 }}>{p.raw_invoices}</div>
                        <div style={{ color: 'var(--muted)' }}>
                          {p.matched_invoice ? `Matched: ${p.matched_invoice}` : 'No match'} · {fmt(p.amount)}
                        </div>
                      </div>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </div>
                    {p.epd_suggestion && p.status !== 'already_imported' && p.status !== 'already_paid' && (
                      <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
                        <span style={{ color: 'var(--muted)' }}>EPD detected: {p.epd_suggestion.epd_pct}% (${p.epd_suggestion.epd_amount.toFixed(2)}) —</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="checkbox"
                            checked={!!epdByRow[p.normalized]?.epd_rate && epdByRow[p.normalized].epd_rate !== '0'}
                            onChange={e => setEpdByRow(f => ({
                              ...f,
                              [p.normalized]: { ...f[p.normalized], epd_rate: e.target.checked ? (p.epd_suggestion.epd_pct / 100).toString() : '0' }
                            }))}
                          />
                          Confirm & apply
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button className="btn btn-secondary" onClick={() => { setStep('upload'); setPreview(null); }}>Back</button>
                <button className="btn btn-primary" onClick={handleCommit} disabled={loading}>
                  {loading ? 'Importing…' : 'Confirm & Import'}
                </button>
              </div>
            </div>
          )}

          {step === 'done' && result && (
            <div>
              <div style={{ background: 'var(--info-bg)', color: 'var(--info-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
                {result.message}
              </div>
              {result.saved_bgv > 0 && (
                <button className="btn btn-secondary" style={{ marginRight: 8 }} onClick={() => { setStep('upload'); setTab('bgv'); setPreview(null); setResult(null); }}>
                  Review {result.saved_bgv} BGV Deduction{result.saved_bgv > 1 ? 's' : ''}
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => { setStep('upload'); setPreview(null); setResult(null); }}>Import Another</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
